/**
 * The linked lache (v1 plan section 8). A link is a transition between bars
 * with no backswing: a bar let go on its forward swing (no backward bottom
 * crossing and no dead hang since the grab) whose flight is caught by another
 * bar. The link counts at that grab, so the first bar of a run, caught from a
 * jump, links too (owner, 2026-09-14). A backswing or a dead hang breaks the run
 * at once, a landing ends it with the count standing, water and restart reset it.
 */

import type { EventBuffer } from "./events";
import { abs, sign } from "./fastmath";
import { startFreeze } from "./grades";
import type { SimState } from "./state";
import type { CompiledTuning } from "./tuning";
import { Ev, LinkEnd } from "./types";

function endRun(s: SimState, ev: EventBuffer, reason: number): void {
  if (s.linkRun > 0) ev.push(Ev.LinkBroken, s.tick, s.linkRun, reason);
  s.linkRun = 0;
}

/**
 * At the grab: a flight from another bar that was let go cleanly is a link. Also sets
 * which way the swing must keep going on this bar. Call before the catch changes the
 * velocity (`vx` is the flight's).
 */
export function linkOnAttach(s: SimState, t: CompiledTuning, ev: EventBuffer, bar: number): void {
  const linked =
    s.linkOpen === 1 &&
    s.lastBarId >= 0 &&
    s.lastBarId !== bar &&
    s.ticksSinceRelease <= t.linkFlightMaxTicks &&
    s.ticksSinceRelease < s.ticksSinceLand;
  s.linkOpen = 0;
  s.linkDir = s.vx !== 0 ? sign(s.vx) : s.facing;
  s.linkSpoiled = 0;
  s.hangTicks = 0;
  if (linked) {
    s.linkRun++;
    const best = s.linkRun > s.linkBest;
    if (best) s.linkBest = s.linkRun;
    ev.push(Ev.Link, s.tick, s.linkRun, bar, best ? 1 : 0);
    if (best) startFreeze(s, t, ev);
  } else endRun(s, ev, s.ticksSinceLand < s.ticksSinceRelease ? LinkEnd.Ground : LinkEnd.Hang);
}

/**
 * At a salmon ladder pop: a climb right after the previous one (no bottom skipped) is a link, the way a
 * clean flight between bars is; the swing keeps going either way, so nothing else changes.
 */
export function linkOnPop(s: SimState, t: CompiledTuning, ev: EventBuffer, bar: number): void {
  if (s.ladderChain < 2) return;
  s.linkRun++;
  const best = s.linkRun > s.linkBest;
  if (best) s.linkBest = s.linkRun;
  ev.push(Ev.Link, s.tick, s.linkRun, bar, best ? 1 : 0);
  if (best) startFreeze(s, t, ev);
}

/**
 * A bottom crossing while attached; `dir` is the swing's direction after it. On a ladder the swing
 * goes both ways by design (a climb at each bottom is the chain), so a crossing there spoils nothing.
 */
export function linkOnBottomCrossed(s: SimState, ev: EventBuffer, dir: number, onLadder = false): void {
  if (onLadder || dir === s.linkDir || dir === 0) return;
  s.linkSpoiled = 1;
  endRun(s, ev, LinkEnd.Backswing);
}

/** Every attached tick: a swing slower than hangOmega for hangBreakMs is a dead hang. */
export function linkOnAttachedTick(s: SimState, t: CompiledTuning, ev: EventBuffer): void {
  if (abs(s.omega) < t.hangOmega) s.hangTicks++;
  else s.hangTicks = 0;
  if (s.hangTicks >= t.hangBreakTicks && s.linkSpoiled === 0) {
    s.linkSpoiled = 1;
    endRun(s, ev, LinkEnd.Hang);
  }
}

/** At the release: a release on the forward swing of an unspoiled grip opens a link to the next bar. */
export function linkOnRelease(s: SimState, ev: EventBuffer): void {
  if (s.linkSpoiled === 0 && s.omega * s.linkDir > 0) s.linkOpen = 1;
  else {
    s.linkOpen = 0;
    endRun(s, ev, LinkEnd.Backswing);
  }
}

/** A landing ends the run; the count stands in linkBest. */
export function linkOnLand(s: SimState, ev: EventBuffer): void {
  endRun(s, ev, LinkEnd.Ground);
}

export function linkOnFail(s: SimState, ev: EventBuffer): void {
  endRun(s, ev, LinkEnd.Fail);
}

/** Restart resets the run and the best. */
export function linkOnRestart(s: SimState, ev: EventBuffer): void {
  endRun(s, ev, LinkEnd.Restart);
  s.linkBest = 0;
}
